import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { orgUiPath } from '@/core/api';
import { useOrgId } from '@/hooks/useOrgId';
import { useCampaigns } from '@/hooks/useCampaigns';
import { useCreatives } from '@/hooks/useCreatives';
import { useAssets } from '@/hooks/useAssets';
import { formatRelativeDate } from '@/utils/date';
import { Icon } from '@/components/primitives/Icon';
import { Badge } from '@/components/primitives/Badge';
import { Skeleton } from '@/components/primitives/Skeleton';
import { creativeEditorLinkState } from '@/rve/editorBackNav';
import { NewCreativeModal } from '@/components/modals/NewCreativeModal';

function SkeletonRow() {
  return (
    <div className="aspectly:flex aspectly:items-center aspectly:justify-between aspectly:p-5 aspectly:bg-surface-container-lowest aspectly:rounded-2xl aspectly:border aspectly:border-outline-variant/10">
      <div className="aspectly:flex aspectly:items-center aspectly:gap-4">
        <Skeleton rounded="xl" className="aspectly:w-11 aspectly:h-11" />
        <div className="aspectly:space-y-2">
          <Skeleton rounded="sm" className="aspectly:h-4 aspectly:w-48" />
          <Skeleton rounded="sm" className="aspectly:h-3 aspectly:w-28" />
        </div>
      </div>
      <Skeleton rounded="full" className="aspectly:h-6 aspectly:w-20" />
    </div>
  );
}

export default function DashboardPage() {
  const orgId = useOrgId();
  const [isNewCreativeOpen, setIsNewCreativeOpen] = useState(false);
  const navigate = useNavigate();
  const campaigns = useCampaigns(5);
  const creatives = useCreatives({ limit: 50 });
  const assets = useAssets(50);

  const campaignCount = campaigns.data?.length ?? 0;
  const creativeCount = creatives.data?.length ?? 0;
  const assetCountText = assets.loading ? '…' : String(assets.data?.length ?? 0);

  const KPI_CARDS = [
    {
      icon: 'auto_awesome',
      label: 'Active Creatives',
      value: creatives.loading ? '…' : String(creativeCount),
      delta: 'Total creatives',
    },
    {
      icon: 'campaign',
      label: 'Total Campaigns',
      value: campaigns.loading ? '…' : String(campaignCount),
      delta: 'In your workspace',
    },
    {
      icon: 'folder',
      label: 'Asset Library',
      value: assetCountText,
      delta: 'Uploaded assets',
    },
  ];

  return (
    <div className="aspectly:p-4 aspectly:md:p-12 aspectly:w-full aspectly:space-y-10">
      {/* Page header */}
      <div className="aspectly:flex aspectly:items-end aspectly:justify-between">
        <div>
          <p className="aspectly:text-[10px] aspectly:font-black aspectly:uppercase aspectly:tracking-widest aspectly:text-primary aspectly:mb-1">
            Overview
          </p>
          <h1 className="aspectly:text-4xl aspectly:font-headline aspectly:font-black aspectly:tracking-tight aspectly:text-on-surface">
            Dashboard
          </h1>
          <p className="aspectly:text-on-surface-variant aspectly:mt-1">
            Welcome back. Here&apos;s what&apos;s happening with your campaigns.
          </p>
        </div>
        {/* 
        // TODO: add back in when we want this button on dashboard page.
        <Button onClick={() => setIsNewCreativeOpen(true)} size="lg">
          <Icon name="add" />
          Creative
        </Button> */}
      </div>

      {/* KPI Cards */}
      <section className="aspectly:grid aspectly:grid-cols-1 aspectly:md:grid-cols-3 aspectly:gap-6">
        {KPI_CARDS.map((card) => (
          <div
            key={card.label}
            className="aspectly:bg-surface-container-lowest aspectly:p-6 aspectly:rounded-2xl aspectly:border aspectly:border-outline-variant/10 aspectly:ambient-shadow aspectly:flex aspectly:flex-col aspectly:justify-between"
          >
            <div className="aspectly:flex aspectly:items-start aspectly:justify-between">
              <div className="aspectly:p-2 aspectly:bg-primary-container aspectly:text-primary aspectly:rounded-xl aspectly:flex aspectly:items-center aspectly:justify-center">
                <Icon name={card.icon} />
              </div>
            </div>
            <div className="aspectly:mt-4">
              <p className="aspectly:text-xs aspectly:text-on-surface-variant aspectly:font-black aspectly:uppercase aspectly:tracking-wider">
                {card.label}
              </p>
              <p className="aspectly:text-4xl aspectly:font-headline aspectly:font-black aspectly:text-on-surface aspectly:mt-1">{card.value}</p>
              <p className="aspectly:text-xs aspectly:text-on-surface-variant aspectly:mt-1">{card.delta}</p>
            </div>
          </div>
        ))}
      </section>

      {/* Recent Campaigns */}
      <section className="aspectly:space-y-4">
        <div className="aspectly:flex aspectly:items-center aspectly:justify-between">
          <h2 className="aspectly:text-xl aspectly:font-headline aspectly:font-bold aspectly:text-on-surface">Recent Campaigns</h2>
          <Link
            to={orgUiPath(orgId, '/campaigns')}
            className="aspectly:text-primary aspectly:text-xs aspectly:font-black aspectly:uppercase aspectly:tracking-wider aspectly:hover:underline"
          >
            View All
          </Link>
        </div>

        {campaigns.error && (
          <div className="aspectly:p-4 aspectly:rounded-xl aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
            {campaigns.error}
          </div>
        )}

        <div className="aspectly:space-y-3">
          {campaigns.loading && [1, 2, 3].map((i) => <SkeletonRow key={i} />)}

          {!campaigns.loading &&
            (campaigns.data ?? []).map((c) => {
              const status: 'Active' | 'Draft' | 'Archived' = 'Active';
              return (
                <Link
                  key={c.id}
                  to={orgUiPath(orgId, `/campaigns/${c.id}`)}
                  className="aspectly:group aspectly:flex aspectly:items-center aspectly:justify-between aspectly:p-5 aspectly:bg-surface-container-lowest aspectly:rounded-2xl aspectly:border aspectly:border-outline-variant/10 aspectly:hover:border-primary/20 aspectly:hover:shadow-md aspectly:transition-all"
                >
                  <div className="aspectly:flex aspectly:items-center aspectly:gap-4">
                    <div className="aspectly:w-11 aspectly:h-11 aspectly:rounded-xl aspectly:bg-primary-container aspectly:flex aspectly:items-center aspectly:justify-center">
                      <Icon name="campaign" className="aspectly:text-primary" />
                    </div>
                    <div>
                      <p className="aspectly:font-bold aspectly:text-on-surface aspectly:group-hover:text-primary aspectly:transition-colors aspectly:font-headline">
                        {c.name}
                      </p>
                      <p className="aspectly:text-xs aspectly:text-on-surface-variant aspectly:mt-0.5">
                        Updated {formatRelativeDate(c.updated_at)}
                      </p>
                    </div>
                  </div>
                  <div className="aspectly:flex aspectly:items-center aspectly:gap-6">
                    <Badge status={status} />
                    <Icon
                      name="chevron_right"
                      className="aspectly:text-on-surface-variant aspectly:group-hover:text-primary aspectly:transition-colors"
                    />
                  </div>
                </Link>
              );
            })}

          {!campaigns.loading && campaigns.data?.length === 0 && (
            <div className="aspectly:py-16 aspectly:text-center">
              <Icon name="campaign" className="aspectly:text-5xl aspectly:text-primary/30" />
              <p className="aspectly:mt-4 aspectly:text-on-surface-variant aspectly:font-medium">No campaigns yet.</p>
              <Link
                to={orgUiPath(orgId, '/campaigns')}
                className="aspectly:mt-4 aspectly:inline-flex aspectly:items-center aspectly:gap-2 aspectly:text-primary aspectly:font-bold aspectly:text-sm aspectly:hover:underline"
              >
                Create your first campaign
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Recent Creatives */}
      <section className="aspectly:space-y-4">
        <div className="aspectly:flex aspectly:items-center aspectly:justify-between">
          <h2 className="aspectly:text-xl aspectly:font-headline aspectly:font-bold aspectly:text-on-surface">Recent Creatives</h2>
          <Link
            to={orgUiPath(orgId, '/creatives')}
            className="aspectly:text-primary aspectly:text-xs aspectly:font-black aspectly:uppercase aspectly:tracking-wider aspectly:hover:underline"
          >
            View All
          </Link>
        </div>

        {creatives.error && (
          <div className="aspectly:p-4 aspectly:rounded-xl aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
            {creatives.error}
          </div>
        )}

        <div className="aspectly:bg-surface-container-lowest aspectly:rounded-2xl aspectly:border aspectly:border-outline-variant/10 aspectly:overflow-hidden">
          <div className="aspectly:grid aspectly:grid-cols-[1fr_160px_100px] aspectly:gap-4 aspectly:px-6 aspectly:py-3 aspectly:bg-surface-container-low/60 aspectly:border-b aspectly:border-outline-variant/10">
            {['Creative', 'Template', 'Updated'].map((h) => (
              <p
                key={h}
                className="aspectly:text-[10px] aspectly:font-black aspectly:uppercase aspectly:tracking-widest aspectly:text-on-surface-variant/60"
              >
                {h}
              </p>
            ))}
          </div>

          {creatives.loading && (
            <div className="aspectly:p-6 aspectly:space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} rounded="lg" className="aspectly:h-10" />
              ))}
            </div>
          )}

          {!creatives.loading &&
            (creatives.data ?? []).slice(0, 5).map((cr) => (
              <Link
                key={cr.id}
                to={orgUiPath(orgId, `/creatives/${cr.id}`)}
                state={creativeEditorLinkState('creatives')}
                className="aspectly:grid aspectly:grid-cols-[1fr_160px_100px] aspectly:gap-4 aspectly:items-center aspectly:px-6 aspectly:py-4 aspectly:border-b aspectly:border-outline-variant/10 aspectly:last:border-0 aspectly:hover:bg-primary-container/20 aspectly:transition-colors aspectly:cursor-pointer"
              >
                <p className="aspectly:font-bold aspectly:text-sm aspectly:text-on-surface aspectly:truncate">{cr.name}</p>
                <p className="aspectly:text-sm aspectly:text-on-surface-variant aspectly:truncate">{cr.template_id}</p>
                <p className="aspectly:text-xs aspectly:text-on-surface-variant">
                  {formatRelativeDate(cr.updated_at)}
                </p>
              </Link>
            ))}

          {!creatives.loading && creatives.data?.length === 0 && (
            <div className="aspectly:py-10 aspectly:text-center">
              <p className="aspectly:text-on-surface-variant aspectly:text-sm">No creatives yet.</p>
            </div>
          )}
        </div>
      </section>

      <NewCreativeModal
        isOpen={isNewCreativeOpen}
        onClose={() => setIsNewCreativeOpen(false)}
        onCreated={(created) =>
          navigate(orgUiPath(orgId, `/creatives/${created.id}`), {
            state: creativeEditorLinkState('creatives'),
          })
        }
      />
    </div>
  );
}
